<template>
    <div v-clickaway="close" class="unread-referral-messages">
        <div v-if="isOpen" class="message-overlay" @click="close()"></div>
        <div @click="isOpen ? close() : open()">
            <common-icon
                class="unread-referral-messages__icon"
                :class="{ 'unread-referral-messages__icon--open': isOpen }"
                name="chat-circle"
                size="gigantic"
            ></common-icon>
            <div class="unread-referral-messages__background"></div>
            <div class="unread-referral-messages__amount">{{ unreadMessagesCount }}</div>
        </div>

        <common-dropdown
            :isOpen="isOpen"
            :hasArrow="isOpen"
            :recalculatePosition="true"
            bgColor="gray"
            arrowPositionY="top"
            arrowPositionX="right"
            class="unread-referral-messages__dropdown"
        >
            <common-content-spacer height="tiny" class="unread-referral-messages__container">
                <div class="unread-referral-messages__tabs">
                    <tabs-input
                        :label="$t('pages.referrals.index.controls.status')"
                        :options="tabs"
                        :value="activeTab"
                        size="small"
                        @change="changeTab($event)"
                    ></tabs-input>
                </div>
                <div class="unread-referral-messages__container--messages">
                    <div v-if="isLoading" class="unread-referral-messages__row">
                        <common-loader :isProcessing="isLoading"></common-loader>
                    </div>
                    <div v-else>
                        <div
                            v-for="(message, index) in getComputedUnreadMessages"
                            :key="index"
                            v-bind="{ route: { name: 'index.home' } }"
                            class="unread-referral-messages__row"
                            @click="goToReferral(message)"
                        >
                            <common-user-avatar
                                :user="{ name: fromUserName(message) }"
                                :isUserProfileLinkEnabled="false"
                                size="user-menu"
                                class="unread-referral-messages__row__avatar"
                            ></common-user-avatar>
                            <div class="unread-referral-messages__row__text">
                                <p>
                                    <b>{{ fromUserName(message) }}</b>
                                </p>
                                <p v-html="message.body.length > 25 ? message.body.slice(0, 22) + '...' : message.body"></p>
                            </div>
                            <p class="unread-referral-messages__row__time">{{ getDateTime(message.createdDateTime) }}</p>
                        </div>
                        <div v-if="!getComputedUnreadMessages.length" class="unread-referral-messages__row">
                            <p>{{ $t('modals.referral_unread_messages.no_new_messages') }}</p>
                        </div>
                    </div>
                </div>
            </common-content-spacer>
        </common-dropdown>
    </div>
</template>

<script>
    import Store from '@/store'
    import MessagesAPI from 'api/MessagesAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import TabsInput from 'components/form-inputs/TabsInput'
    import dayjs from 'dayjs'

    export default {
        components: { TabsInput },
        data() {
            return {
                isOpen: false,
                userId: null,
                unreadMessages: [],
                unreadPatientMessages: [],
                PracticeTypeEnum: PracticeTypeEnum,
                activeTab: 'providers',
                isLoading: false,
            }
        },

        computed: {
            unreadMessagesCount() {
                return Store.getters['chat-messages/getUnread'].length
            },

            getComputedUnreadMessages() {
                return this.activeTab === 'providers' ? this.unreadMessages : this.unreadPatientMessages
            },

            tabs() {
                return [
                    {
                        value: 'providers',
                        label: 'Providers',
                    },
                    {
                        value: 'patients',
                        label: 'Patients',
                    },
                ]
            },
        },

        methods: {
            fromUserName(message) {
                if (this.activeTab === 'providers') {
                    if (message.fromUser) {
                        const name = `${message.fromUser.firstName || ''} ${message.fromUser.lastName || ''}`.trim()
                        return message.fromUser.designation ? `${name}, ${message.fromUser.designation}` : name
                    }
                    return message.practice?.name || ''
                } else {
                    return message.fromPatient
                        ? message.fromPatient.firstName + ' ' + message.fromPatient.lastName
                        : message.practice?.name || ''
                }
            },
            getDateTime(createdDateTime) {
                const now = dayjs()
                return dayjs(createdDateTime).isSame(now, 'day')
                    ? dayjs(createdDateTime).format('hh:mm a')
                    : dayjs(createdDateTime).format('MM.DD.YYYY')
            },
            close() {
                if (this.isLoading) return
                this.isOpen = false
            },
            changeTab(event) {
                this.activeTab = event
            },
            open() {
                this.isLoading = true
                Promise.all([this.getUnreadMessages(), this.getPatientUnreadMessages()])
                    .then(([unreadMessages, unreadPatientMessages]) => {
                        const mergedMessages = unreadMessages.concat(unreadPatientMessages)
                        Store.dispatch('chat-messages/setUnread', mergedMessages)
                        this.unreadMessages = unreadMessages
                        this.unreadPatientMessages = unreadPatientMessages
                        this.isOpen = true
                    })
                    .finally(() => {
                        this.isLoading = false
                    })

                this.isOpen = true
            },

            goToReferral(message) {
                let type = this.$auth.getUser().id === message.properties.referralFromUserId ? 'made' : 'received'
                this.$router.push({
                    name: `referrals.${type}.show`,
                    params: { id: message.objectId },
                    query: { openChat: true, chatType: this.activeTab },
                })
                this.close()
            },
            getUnreadMessages() {
                const _fields = [
                    'objectId',
                    'body',
                    'fromUser(id,designation,avatar,firstName,lastName,userRole(id,name))',
                    'properties(referralFromUserId,referralToUserId)',
                    'practice(name)',
                    'createdDateTime',
                ]
                const query = {
                    toUserId:
                        this.$auth.getPractice().practiceType === this.PracticeTypeEnum.ANCILLARY
                            ? 0
                            : this.$auth.getActiveMember().id,
                    toPracticeId: this.$auth.getPractice().id,
                }
                return MessagesAPI.getUnread(query, _fields).then((resp) => {
                    return resp
                })
            },

            getPatientUnreadMessages() {
                const _fields = [
                    'objectId',
                    'body',
                    'fromPatient(id,firstName,lastName,profilePicture)',
                    'properties(referralFromUserId,referralToUserId)',
                    'practice(name)',
                    'createdDateTime',
                ]
                const query = {
                    toUserId:
                        this.$auth.getPractice().practiceType === this.PracticeTypeEnum.ANCILLARY
                            ? 0
                            : this.$auth.getActiveMember().id,
                    toPracticeId: this.$auth.getPractice().id,
                }
                return MessagesAPI.getUnread(query, _fields).then((resp) => {
                    return resp
                })
            },
        },
    }
</script>
